/**
 * AI data consent + reports of AI replies (store review).
 *
 *   POST /api/auth/me/ai-data-consent {version}         -> 200 {user} (409 stale_consent_version)
 *   POST /api/modules/english/ai-content-reports {...}  -> 201 {ok,id,createdAt} (400, 404, 429)
 *
 * The backend refuses speak's AI features without a consent for the CURRENT
 * text (`AI_DATA_CONSENT_VERSION`, fail closed); the consent screen sends the
 * version it showed. Contract types live in `@orborix/shared-types` (ai-data.ts).
 */
import { createTypedApiClient, type AuthUser } from '@orborix/auth-client';
import { AI_DATA_CONSENT_VERSION, type AiContentReportInput } from '@orborix/shared-types';

const client = createTypedApiClient({
  basePath: '',
  errorName: 'AiDataApiError',
  // No call here takes a `signal`.
  abortError: 'wrap',
});

export const AiDataApiError = client.ApiError;
export type AiDataApiError = InstanceType<typeof client.ApiError>;

/**
 * `true` while the account still has to accept the AI data consent. Absent
 * (an old backend, a fixture) reads as required: fail closed.
 */
export function needsAiDataConsent(user: Pick<AuthUser, 'aiDataConsentRequired'>): boolean {
  return user.aiDataConsentRequired !== false;
}

/** Records the consent for the text this bundle shows. */
export function acceptAiDataConsent(): Promise<{ user: AuthUser }> {
  return client.send<{ user: AuthUser }>('/api/auth/me/ai-data-consent', {
    method: 'POST',
    body: JSON.stringify({ version: AI_DATA_CONSENT_VERSION }),
  });
}

/** Flags an AI reply. */
export function reportAiContent(
  input: AiContentReportInput,
): Promise<{ ok: true; id: string; createdAt: string }> {
  return client.send<{ ok: true; id: string; createdAt: string }>(
    '/api/modules/english/ai-content-reports',
    { method: 'POST', body: JSON.stringify(input) },
  );
}
